import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Copy, FileDown, FileText, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  TableColumnVisibilityMenu,
  initialColumnVisibility,
  countVisibleColumns,
} from '@/components/shared/TableColumnVisibilityMenu'
import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { useAuthStore } from '@/store/auth.store'

import { scPurchaseOrderService, type ScPurchaseOrderListRow } from '@/services/scPurchaseOrderService'

const PAGE_SIZE = 10

type ColumnKey = 'code' | 'reference' | 'requisitionCode' | 'vendorName' | 'orderDate' | 'estimatedDeliveryDate' | 'total'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'code', labelKey: 'purchaseOrders.code' },
  { key: 'reference', labelKey: 'purchaseOrders.reference' },
  { key: 'requisitionCode', labelKey: 'purchaseRequisitions.columns.code' },
  { key: 'vendorName', labelKey: 'purchaseOrders.vendors' },
  { key: 'orderDate', labelKey: 'purchaseOrders.orderDate' },
  { key: 'estimatedDeliveryDate', labelKey: 'purchaseOrders.expectedDeliveryDate' },
  { key: 'total', labelKey: 'purchaseOrders.total' },
]

function fmtDate(value?: string, lang?: string) {
  if (!value) return '—'
  const d = new Date(`${value}T12:00:00`)
  if (Number.isNaN(d.getTime())) return value
  const locale = lang?.startsWith('ar') ? 'ar-SA' : 'en-GB'
  return d.toLocaleDateString(locale)
}

export const SupplyChainPurchaseOrdersList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)

  const [searchQuery, setSearchQuery] = useState('')
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    initialColumnVisibility(ALL_COLUMNS.map((c) => c.key)),
  )
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCountFromApi, setTotalCountFromApi] = useState(0)
  const [rows, setRows] = useState<ScPurchaseOrderListRow[]>([])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await scPurchaseOrderService.getAll(branchId, pageNumber, PAGE_SIZE)
        setRows(data.items)
        setTotalCountFromApi(data.totalCount)
      } catch (error: any) {
        const status = error?.response?.status
        const msg = String(error?.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setRows([])
        setTotalCountFromApi(0)
        if (!isNoData) toast.error(error?.response?.data?.message || t('purchaseOrders.failedToLoadList'))
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, pageNumber, t])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.code, r.reference, r.requisitionCode, r.vendorName, r.orderDate, r.estimatedDeliveryDate, String(r.total)].some((f) =>
        String(f ?? '').toLowerCase().includes(q),
      ),
    )
  }, [rows, searchQuery])

  const totalCount = searchQuery.trim() ? filtered.length : totalCountFromApi
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const page = Math.min(pageNumber, totalPages)
  const start = (page - 1) * PAGE_SIZE
  const pageSlice = searchQuery.trim() ? filtered.slice(start, start + PAGE_SIZE) : filtered

  const toggleColumn = (id: string) => {
    const key = id as ColumnKey
    if (!ALL_COLUMNS.some((c) => c.key === key)) return
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const columnMenuItems = useMemo(
    () => ALL_COLUMNS.map((c) => ({ id: c.key, label: t(c.labelKey) })),
    [t],
  )

  const visibleCount = countVisibleColumns(visibleColumns)
  const tableColSpan = visibleCount + 1

  const handleCopy = () => {
    if (filtered.length === 0) return toast.error(t('purchaseRequisitions.nothingToCopy'))
    const header = ALL_COLUMNS.filter((c) => visibleColumns[c.key]).map((c) => t(c.labelKey))
    const lines = filtered.map((r) => {
      const cells: string[] = []
      if (visibleColumns.code) cells.push(r.code)
      if (visibleColumns.reference) cells.push(r.reference)
      if (visibleColumns.requisitionCode) cells.push(r.requisitionCode)
      if (visibleColumns.vendorName) cells.push(r.vendorName)
      if (visibleColumns.orderDate) cells.push(r.orderDate)
      if (visibleColumns.estimatedDeliveryDate) cells.push(r.estimatedDeliveryDate)
      if (visibleColumns.total) cells.push(String(Number(r.total || 0).toFixed(2)))
      return cells.join('\t')
    })
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('purchaseRequisitions.copied'))
  }

  const csvEscape = (cell: string) => `"${cell.replace(/"/g, '""')}"`

  const handleExportCSV = () => {
    if (filtered.length === 0) return toast.error(t('purchaseRequisitions.nothingToExport'))
    const header = ALL_COLUMNS.filter((c) => visibleColumns[c.key])
      .map((c) => csvEscape(t(c.labelKey)))
      .join(',')
    const body = pageSlice
      .map((r) => {
        const cells: string[] = []
        if (visibleColumns.code) cells.push(r.code)
        if (visibleColumns.reference) cells.push(r.reference)
        if (visibleColumns.requisitionCode) cells.push(r.requisitionCode)
        if (visibleColumns.vendorName) cells.push(r.vendorName)
        if (visibleColumns.orderDate) cells.push(r.orderDate)
        if (visibleColumns.estimatedDeliveryDate) cells.push(r.estimatedDeliveryDate)
        if (visibleColumns.total) cells.push(String(Number(r.total || 0).toFixed(2)))
        return cells.map(csvEscape).join(',')
      })
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `supply_chain_purchase_orders_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('purchaseRequisitions.exported'))
  }

  const handleDelete = async (r: ScPurchaseOrderListRow) => {
    if (!branchId) {
      toast.error(t('purchaseOrders.pleaseSelectBranch'))
      return
    }
    const ok = await showDeleteConfirm(r.code)
    if (!ok) return
    try {
      await scPurchaseOrderService.delete(r.id, branchId)
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('common.failed'))
      return
    }
    await showSuccess(t('common.success'), t('purchaseRequisitions.deleted'))
    setPageNumber(1)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('purchaseOrders.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('purchaseOrders.listSubtitle')}</p>
          </div>
        </div>

        <Button onClick={() => navigate('/supply-chain/purchase-orders/new')} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('purchaseOrders.addOrder')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">{t('purchaseRequisitions.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('purchaseRequisitions.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <TableColumnVisibilityMenu
                columns={columnMenuItems}
                visibility={visibleColumns}
                onToggle={toggleColumn}
                triggerLabel={t('purchaseRequisitions.columnVisibility')}
                menuTitle={t('purchaseRequisitions.columnsToggle')}
              />
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('purchaseRequisitions.copy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {visibleColumns.code && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseOrders.code')}
                    </TableHead>
                  )}
                  {visibleColumns.reference && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseOrders.reference')}
                    </TableHead>
                  )}
                  {visibleColumns.requisitionCode && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseRequisitions.columns.code')}
                    </TableHead>
                  )}
                  {visibleColumns.vendorName && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseOrders.vendors')}
                    </TableHead>
                  )}
                  {visibleColumns.orderDate && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseOrders.orderDate')}
                    </TableHead>
                  )}
                  {visibleColumns.estimatedDeliveryDate && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseOrders.expectedDeliveryDate')}
                    </TableHead>
                  )}
                  {visibleColumns.total && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseOrders.total')}
                    </TableHead>
                  )}
                  <TableHead className="h-12 w-[110px] bg-primary/5 px-2 text-center font-bold text-primary">
                    {t('purchaseRequisitions.columns.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : pageSlice.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      {t('purchaseOrders.noDataInTable')}
                    </TableCell>
                  </TableRow>
                ) : (
                  pageSlice.map((r) => (
                    <TableRow key={r.id} className="border-b transition-colors hover:bg-primary/5">
                      {visibleColumns.code && <TableCell className="px-4 py-2 text-center text-sm">{r.code}</TableCell>}
                      {visibleColumns.reference && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.reference || '—'}</TableCell>
                      )}
                      {visibleColumns.requisitionCode && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.requisitionCode || '—'}</TableCell>
                      )}
                      {visibleColumns.vendorName && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.vendorName || '—'}</TableCell>
                      )}
                      {visibleColumns.orderDate && (
                        <TableCell className="px-4 py-2 text-center text-sm">{fmtDate(r.orderDate, i18n.language)}</TableCell>
                      )}
                      {visibleColumns.estimatedDeliveryDate && (
                        <TableCell className="px-4 py-2 text-center text-sm">
                          {fmtDate(r.estimatedDeliveryDate, i18n.language)}
                        </TableCell>
                      )}
                      {visibleColumns.total && (
                        <TableCell className="px-4 py-2 text-center text-sm">{Number(r.total || 0).toFixed(2)}</TableCell>
                      )}
                      <TableCell className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-0.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-primary/10"
                            title={t('purchaseRequisitions.edit')}
                            aria-label={t('purchaseRequisitions.edit')}
                            onClick={() => navigate(`/supply-chain/purchase-orders/${r.id}/edit`)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            title={t('purchaseRequisitions.delete')}
                            aria-label={t('purchaseRequisitions.delete')}
                            onClick={() => void handleDelete(r)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="p-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {t('purchaseOrders.showingEntries', {
                from: totalCount === 0 ? 0 : start + 1,
                to: Math.min(start + PAGE_SIZE, totalCount),
                total: totalCount,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPageNumber((p) => Math.max(1, p - 1))}>
                {t('purchaseOrders.prev')}
              </Button>
              <span>{t('purchaseOrders.paginationPage', { current: page, total: totalPages })}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
              >
                {t('purchaseOrders.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

